import { PropsWithChildren, useRef } from "react";
import { ScrollRestoration } from "react-router-dom";
import { Loading } from "@/components/atoms";
import {
  BackToQuickSetupBanner,
  ConfigProvider,
  Head,
  InactiveAccountBanner,
  LayoutProvider,
  PaymentConfigurationBanner,
  PlanSelectionModal,
  PrivateContent,
  Sidebar,
  TrialSubscriptionBanner,
  VehicleMigrationBanner,
  UpgradedPlanWelcomeModal,
  WelcomeOverlay,
  SubscriptionActiveOverlay,
} from "@/components/organisms";
import { useWelcomeOverlay, useSubscriptionAssignment, useSubscriptionActiveOverlay, useOperatorAccess } from "@/hooks";
import { useSetupAccount } from "./hooks/useSetupAccount";

export const Private = () => {
  const { showWelcome, userType, handleClose } = useWelcomeOverlay();
  const { showOverlay: showSubscriptionActiveOverlay, handleClose: handleSubscriptionActiveOverlayClose } = useSubscriptionActiveOverlay();

  // Load subscription data at top level to prevent unmount/remount cycles
  const { subscriptionStatus, planSelectionModal, preSelectedPlanId, markWelcomeModalAsShown, closePlanSelection } = useSubscriptionAssignment();
  const { isUnsubscribed } = useOperatorAccess();

  const handleUpgradedPlanWelcomeModalClose = () => {
    markWelcomeModalAsShown();
  };

  return (
    <ConfigProvider>
      <AccountProvider>
        <LayoutProvider>
          <Head />
          <ScrollRestoration />
          <InactiveAccountBanner />
          <PaymentConfigurationBanner />
          <TrialSubscriptionBanner />
          <VehicleMigrationBanner />
          <BackToQuickSetupBanner />
          <Sidebar className="hidden lg:flex" />
          <PrivateContent />
          {userType && <WelcomeOverlay open={showWelcome} userType={userType} onClose={handleClose} />}
          {/* Subscription Active Overlay - shown after successful subscription activation */}
          <SubscriptionActiveOverlay open={showSubscriptionActiveOverlay} onClose={handleSubscriptionActiveOverlayClose} />
          {/* Upgraded Plan Welcome Modal - shown for existing users in trial period */}
          <UpgradedPlanWelcomeModal
            open={subscriptionStatus.shouldShowWelcomeModal}
            onClose={handleUpgradedPlanWelcomeModalClose}
            planName={subscriptionStatus.baseSubscription?.subscription.name ?? "Enterprise"}
            trialDaysLeft={subscriptionStatus.trialDaysLeft ?? 30}
            trialStartDate={subscriptionStatus.baseSubscription?.startDate}
            trialEndDate={subscriptionStatus.trialEndsAt}
          />
          {/* Plan Selection Modal - shown when trial/subscription ends or user clicks "Start Subscription" */}
          {/* Note: Modal is non-closable for trial-ended and subscription-ended variants. */}
          {/* When unsubscribed, the inactive banner + Subscription Required lock pane replace the auto modal. */}
          {planSelectionModal.variant && (!isUnsubscribed || planSelectionModal.variant === "activate") && (
            <PlanSelectionModal
              open={planSelectionModal.shouldShow}
              onClose={planSelectionModal.variant === "activate" ? closePlanSelection : undefined}
              variant={planSelectionModal.variant}
              planName={subscriptionStatus.baseSubscription?.subscription.name ?? "Enterprise"}
              currentSubscriptionId={subscriptionStatus.baseSubscription?.subscription.subscriptionId}
              preSelectedPlanId={preSelectedPlanId}
            />
          )}
        </LayoutProvider>
      </AccountProvider>
    </ConfigProvider>
  );
};

const AccountProvider = ({ children }: PropsWithChildren) => {
  const { isReady } = useSetupAccount();
  const hasLoadedOnce = useRef(false);

  // Track when data has loaded at least once in this session
  if (isReady) {
    hasLoadedOnce.current = true;
  }

  // Show loading only on initial mount, not on subsequent refetches
  if (!isReady && !hasLoadedOnce.current) {
    return <Loading />;
  }

  return <>{children}</>;
};
